import { EventBarrier } from "@/components/common/event-barrier";
import { useMaxWidthBetweenSideToolbars } from "@/components/common/ui-overlay-provider";
import { selectActiveMeasurement } from "@/store/measurement-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import {
  FaroButton,
  HistoryRedoIcon,
  HistoryUndoIcon,
  Toolbar,
  ToolButton,
  ToolGroup,
} from "@faro-lotv/flat-ui";
import { useMediaQuery } from "@mui/material";
import type { JSX } from "react";
import { useMeasurementGuidanceStore } from "./measurement-guidance-store";

/**
 * @returns the bottom toolbar to undo, redo and complete the measurement being taken, replacing
 * the keyboard shortcuts on touch devices
 */
export function MeasurementTouchToolbar(): JSX.Element | null {
  const isMeasuring = useAppSelector(selectActiveTool) === ToolName.measurement;
  const isTouch = useMediaQuery("(pointer: coarse)");
  const { livePointsCount, canRedo, actions } = useMeasurementGuidanceStore();
  const maxWidth = useMaxWidthBetweenSideToolbars();
  // A completed measurement stays selected until the first point of the next one is placed
  const hasCompletedMeasurement = !!useAppSelector(selectActiveMeasurement);

  if (!isMeasuring || !isTouch || !actions || hasCompletedMeasurement) {
    return null;
  }

  return (
    <EventBarrier>
      <Toolbar direction="row" sx={{ alignItems: "center", maxWidth }}>
        <ToolGroup sx={{ px: 1, flexDirection: "row", gap: 1 }}>
          <ToolButton
            aria-label="undo measurement point"
            disabled={livePointsCount === 0}
            onClick={actions.undo}
          >
            <HistoryUndoIcon />
          </ToolButton>
          <ToolButton
            aria-label="redo measurement point"
            disabled={!canRedo}
            onClick={actions.redo}
          >
            <HistoryRedoIcon />
          </ToolButton>
        </ToolGroup>
        <ToolGroup sx={{ p: 1 }}>
          <FaroButton
            aria-label="finish measurement"
            variant="primary"
            sx={{ flexShrink: 0 }}
            disabled={livePointsCount < 2}
            onClick={actions.finish}
            dark
          >
            Done
          </FaroButton>
        </ToolGroup>
      </Toolbar>
    </EventBarrier>
  );
}
